import type { Availability } from "./my-availability";
import {
  getBreakState,
  setBreakState,
  clearBreakState,
  type ActiveBreakState,
} from "./break-state";

/** Detalhe da pausa publicado pelo motor (activeBreak do snapshot). */
export interface BreakDetalhe {
  reason: string | null;
  startedAt: string | null;
  /** Limite do motivo, quando o snapshot o resolveu (`activeBreak.maxSeconds`). */
  maxSeconds?: number | null;
}

/** Payload do `agent.break.updated` (gk-chat#280) — so os campos que usamos. */
interface BreakEvent {
  status?: string;
  intent_active?: boolean;
  break_reason_name?: string | null;
  started_at?: string | null;
}

/**
 * Reconcilia o estado LOCAL de pausa contra a disponibilidade publicada pelo
 * motor. O motor e a fonte da verdade do "esta em pausa?" (issue #450); o
 * estado local guarda apenas o detalhe que o snapshot nao devolve — motivo e
 * inicio, ja que `GET /chat/me/availability` sempre volta com
 * `activeBreak: null` (indice de pausas e admin-only no fork).
 *
 * - motor diz != on_break  -> descarta o local. Era o bug da #450: a pausa da
 *   TCNET foi encerrada em silencio pelo logout (`update_all`, sem evento) e a
 *   tela seguiu contando por 16h.
 * - motor diz on_break     -> segue o motor. Logar ja em pausa passa a ser
 *   estado legitimo com a gk-chat#280 (pausa + logout + login devolve o agente
 *   EM PAUSA); contradizer o motor aqui seria o bug espelhado, e erraria pro
 *   lado perigoso — agente se acha disponivel estando fora da fila.
 * - motor desconhecido     -> preserva o local. Quem some do `presence.update`
 *   (aba fechada/throttled) nao vem marcado como "saiu da pausa";
 *   `get_available_user_ids` lista so quem tem presenca viva no Redis.
 *   Ausencia e desconhecido, nunca fim de pausa.
 */
export function reconcileBreakState(
  motor: Availability | null,
  local: ActiveBreakState | null,
  doMotor?: BreakDetalhe | null,
): ActiveBreakState | null {
  if (motor == null) return local;
  if (motor !== "on_break") return null;
  // `doMotor` e o activeBreak do snapshot (gk-chat#280): o motor publica o
  // inicio da pausa no evento `agent.break.updated`, o backend guarda, e ele
  // volta aqui. Vence o local por ser a fonte da verdade — inclusive pra quem
  // abre a plataforma ja em pausa, que e o comportamento novo do login.
  // Sem ele, pausa iniciada em outra sessao aparece sem duracao: sabemos que
  // ela existe, nao quando comecou, e fingir que comecou agora seria pior.
  if (doMotor?.startedAt) {
    const reason = doMotor.reason ?? local?.reason ?? null;
    // Snapshot sem limite resolvido so herda o local se for a MESMA pausa de
    // motivo — mesma regra do `reduceBreakEvent`: a pausa antiga pode ter sido
    // encerrada sem evento (logout, job de limpeza) e trocada por outra.
    const maxSeconds =
      doMotor.maxSeconds !== undefined
        ? doMotor.maxSeconds
        : local && local.reason === reason
          ? local.maxSeconds
          : undefined;
    return {
      reason,
      startedAt: doMotor.startedAt,
      // `minimized` e escolha de UI do agente naquele navegador — o motor nao
      // tem opiniao sobre isso.
      minimized: local?.minimized ?? false,
      ...(maxSeconds !== undefined ? { maxSeconds } : {}),
    };
  }
  return local ?? { reason: null, startedAt: null, minimized: false };
}

/**
 * Reduz o evento `agent.break.updated` (gk-chat#280) contra o estado local.
 *
 * ⚠️ `status: 'ended'` com `intent_active: true` NAO encerra a pausa: quem
 * encerrou a sessao foi o job de limpeza ou o logout, e so a MEDICAO parou — a
 * intencao do agente segue de pe em `account_users.break_intent_at`, e so ele
 * a limpa. Tratar `ended` como fim reintroduz a #450 pela porta nova, agora
 * com a tela dizendo "disponivel" pra quem esta fora da fila.
 */
export function reduceBreakEvent(
  evento: unknown,
  local: ActiveBreakState | null,
): ActiveBreakState | null {
  const e = evento as BreakEvent | null | undefined;
  if (!e || typeof e !== "object") return local;

  if (e.status === "ended") {
    return e.intent_active === true ? local : null;
  }
  if (e.status !== "started" || !e.started_at) return local;

  const reason = e.break_reason_name ?? local?.reason ?? null;
  // O evento do motor nao traz o limite (e metadado da Platform). So o herda
  // do local quando e o MESMO motivo — trocar de motivo com o limite do
  // anterior avisaria estouro errado. A resposta do start_break repoe o certo.
  const maxSeconds = local && local.reason === reason ? local.maxSeconds : undefined;
  return {
    reason,
    startedAt: e.started_at,
    minimized: local?.minimized ?? false,
    ...(maxSeconds !== undefined ? { maxSeconds } : {}),
  };
}

/**
 * Aplica a reconciliacao no estado persistido. Ponto unico de escrita a partir
 * do que o motor reporta — usado no boot (snapshot) e no realtime
 * (`gk:chat:my-availability`). No-op quando nada muda, pra nao disparar
 * BREAK_EVENT a toa a cada broadcast de presenca.
 */
export function applyBreakReconciliation(
  motor: Availability | null,
  doMotor?: BreakDetalhe | null,
): void {
  const local = getBreakState();
  const next = reconcileBreakState(motor, local, doMotor);
  if (next === local) return;
  if (!next) {
    if (local) clearBreakState();
    return;
  }
  setBreakState(next);
}

/** Aplica o evento `agent.break.updated` no estado persistido. Mesmo contrato
 *  de no-op do applyBreakReconciliation. */
export function applyBreakEvent(evento: unknown): void {
  const local = getBreakState();
  const next = reduceBreakEvent(evento, local);
  if (next === local) return;
  if (!next) {
    if (local) clearBreakState();
    return;
  }
  setBreakState(next);
}
